/**
 * Automation rules — the player's programmable conditions for the greenhouse's
 * automated systems (the computer terminal). The FIRST system is grow-light control,
 * but the model is generic so future systems (sprinklers, vents…) reuse it.
 *
 * A RULE is: a list of CONDITIONS + an AND/OR combiner. Each condition is
 * `{ field, op, value }` — e.g. wind >= breezy, weather == overcast. Fields expose a
 * curated set of preset VALUES and the operators that make sense for them. Evaluation
 * is pure: `evalRule(rule, ctx)` → boolean, given a live `AutomationContext` snapshot.
 *
 * For lights specifically: the light is ON when its rule passes (no conditions ⇒
 * always true ⇒ on-when-powered) AND the grid pole is live.
 */

import type { WeatherType } from "./weather";

/** `every` is the SPRINKLERS' schedule condition ("Every X minutes") — not a state of the world but a clock:
 *  its value is a number of minutes (as a string, like every other value). Rule evaluation treats it as always
 *  true; the sprinkler run model (useGreenhouse) reads its period with `everyMinutes` and fires an activation
 *  that often while the rule's other conditions hold. Lights never offer it (FIELD_LIST vs SPRINKLER_FIELD_LIST). */
export type ConditionField = "timeOfDay" | "wind" | "weather" | "every";
export type Operator = "eq" | "ne" | "ge" | "le" | "gt" | "lt";

export interface Condition {
  field: ConditionField;
  op: Operator;
  value: string;          // a key from the field's preset list
}

export type Combiner = "and" | "or";

export interface Rule {
  combiner: Combiner;
  conditions: Condition[];
}

export function emptyRule(): Rule {
  return { combiner: "and", conditions: [] };
}

// ---- field + operator definitions (drive the dropdowns) --------------------

export interface FieldDef {
  key: ConditionField;
  label: string;
  /** Ordered preset values: { key, label, ordinal }. Ordinal gives </<=/>=/>
   *  comparisons a meaningful order (e.g. Dawn<Day<Dusk<Night, 0<25<50<…). */
  values: Array<{ key: string; label: string; ordinal: number }>;
  /** Whether ordered operators (≥ ≤ > <) apply (numeric/ordered fields) or only =/≠. */
  ordered: boolean;
}

// NOTE: a saved rule may still carry a value string for a REMOVED type (`bright`). It is left as-is
// rather than migrated: `eq` simply never matches it, `ne` always does, and ordinalOf falls back to 0 —
// so the rule keeps working, it just stops firing on a condition that no longer exists. The editor's
// dropdown renders it as the unknown-value row below.
const WEATHER_VALUES: Array<{ key: WeatherType; label: string }> = [
  { key: "clear", label: "Clear Skies" },
  { key: "fog", label: "Fog" },
  { key: "partlyCloudy", label: "Partly Cloudy" },
  { key: "mostlyCloudy", label: "Mostly Cloudy" },
  { key: "overcast", label: "Overcast" },
  { key: "lightRain", label: "Light Rain" },
  { key: "heavyRain", label: "Heavy Rain" },
  { key: "thunderstorm", label: "Thunderstorm" },
];

export const FIELDS: Record<ConditionField, FieldDef> = {
  timeOfDay: {
    key: "timeOfDay", label: "Time of day", ordered: true,
    values: [
      { key: "dawn", label: "Dawn", ordinal: 0 },
      { key: "day", label: "Day", ordinal: 1 },
      { key: "dusk", label: "Dusk", ordinal: 2 },
      { key: "night", label: "Night", ordinal: 3 },
    ],
  },
  wind: {
    key: "wind", label: "Wind speed", ordered: true,
    values: [
      { key: "calm", label: "Calm (0–8 mph)", ordinal: 0 },
      { key: "breezy", label: "Breezy (9–20 mph)", ordinal: 1 },
      { key: "strong", label: "Strong (21–35 mph)", ordinal: 2 },
      { key: "gale", label: "Gale (36+ mph)", ordinal: 3 },
    ],
  },
  weather: {
    key: "weather", label: "Weather", ordered: false,
    values: WEATHER_VALUES.map((w, i) => ({ key: w.key, label: w.label, ordinal: i })),
  },
  // no presets: the value is typed minutes (see EVERY_MIN/EVERY_MAX); the editor renders a number box for it
  every: { key: "every", label: "Every X minutes", ordered: false, values: [] },
};

/** The world-state fields — what a grow light's rule may use. */
export const FIELD_LIST: FieldDef[] = [FIELDS.timeOfDay, FIELDS.wind, FIELDS.weather];
/** A sprinkler's rule may also carry the "Every X minutes" schedule. */
export const SPRINKLER_FIELD_LIST: FieldDef[] = [...FIELD_LIST, FIELDS.every];
/** Bounds and default for an "Every X minutes" period, in real minutes. */
export const EVERY_MIN = 1, EVERY_MAX = 240, EVERY_DEFAULT = 5;
/** Parse an "every" condition's typed minutes, clamped into range (a hand-edited save can hold anything). */
export function clampEveryMinutes(value: string | number): number {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(EVERY_MIN, Math.min(EVERY_MAX, n)) : EVERY_DEFAULT;
}
/** A rule's schedule period in minutes (its first "every" condition), or null when it has none. */
export function everyMinutes(rule: Rule | undefined): number | null {
  const c = rule?.conditions.find((x) => x.field === "every");
  return c ? clampEveryMinutes(c.value) : null;
}

export const OPERATORS: Array<{ key: Operator; label: string; symbol: string; orderedOnly: boolean }> = [
  { key: "eq", label: "is", symbol: "=", orderedOnly: false },
  { key: "ne", label: "is not", symbol: "≠", orderedOnly: false },
  { key: "ge", label: "at least", symbol: "≥", orderedOnly: true },
  { key: "le", label: "at most", symbol: "≤", orderedOnly: true },
  { key: "gt", label: "above", symbol: ">", orderedOnly: true },
  { key: "lt", label: "below", symbol: "<", orderedOnly: true },
];

/** Operators valid for a given field (all for ordered fields; =/≠ only otherwise). */
export function operatorsFor(field: ConditionField): typeof OPERATORS {
  if (field === "every") return OPERATORS.filter((o) => o.key === "eq");   // a schedule has no "is not"
  const ordered = FIELDS[field].ordered;
  return OPERATORS.filter((o) => ordered || !o.orderedOnly);
}

/** The ordinal of a value key within its field (for ordered comparisons). */
function ordinalOf(field: ConditionField, valueKey: string): number {
  return FIELDS[field].values.find((v) => v.key === valueKey)?.ordinal ?? 0;
}

// ---- live context + evaluation ---------------------------------------------

/** A snapshot of the live world the conditions compare against. */
export interface AutomationContext {
  timeOfDay: "dawn" | "day" | "dusk" | "night";
  windMph: number;
  weather: WeatherType;
}

/** Map a wind mph to its band ordinal (matches the `wind` presets). */
function windOrdinal(mph: number): number {
  if (mph <= 8) return 0;
  if (mph <= 20) return 1;
  if (mph <= 35) return 2;
  return 3;
}

/** The live ordinal/key for a field from the context.
 *  `key` MUST be one of the field's preset keys, because `eq`/`ne` compare it against the
 *  key the editor stored. Wind used to return the band ORDINAL as a string ("0".."3"),
 *  which matches no preset ("calm".."gale") — so "wind is Breezy" never fired and "wind is
 *  not Calm" always did. It returns the band's preset key now. */
function liveValue(field: ConditionField, ctx: AutomationContext): { ord: number; key: string } {
  switch (field) {
    case "timeOfDay": return { ord: ordinalOf("timeOfDay", ctx.timeOfDay), key: ctx.timeOfDay };
    case "wind": {
      const ord = windOrdinal(ctx.windMph);
      return { ord, key: FIELDS.wind.values[ord]!.key };
    }
    case "weather": return { ord: ordinalOf("weather", ctx.weather), key: ctx.weather };
    case "every": return { ord: 0, key: "" };   // never compared (evalCondition short-circuits it)
  }
}

/** Evaluate one condition against the live context. */
function evalCondition(c: Condition, ctx: AutomationContext): boolean {
  if (c.field === "every") return true;   // a schedule, not a state — applied by the sprinkler run model
  const live = liveValue(c.field, ctx);
  if (c.op === "eq") return live.key === c.value;
  if (c.op === "ne") return live.key !== c.value;
  const target = ordinalOf(c.field, c.value);
  switch (c.op) {
    case "ge": return live.ord >= target;
    case "le": return live.ord <= target;
    case "gt": return live.ord > target;
    case "lt": return live.ord < target;
    default: return true;
  }
}

/** Evaluate a whole rule (AND/OR over its conditions). No conditions ⇒ true. */
export function evalRule(rule: Rule | undefined, ctx: AutomationContext): boolean {
  if (!rule || rule.conditions.length === 0) return true;
  return rule.combiner === "and"
    ? rule.conditions.every((c) => evalCondition(c, ctx))
    : rule.conditions.some((c) => evalCondition(c, ctx));
}

/** Drop every condition on a field that no longer exists from a SAVED rule map, keeping the rule.
 *  The `battery` field went with the battery itself; a rule like "battery ≥ 50 AND night" becomes
 *  "night" — the closest thing to what the player wrote that can still run. A rule left with no
 *  conditions reads as on-when-powered, same as any empty rule. Anything that is not rule-shaped is
 *  passed through untouched for the loader's other checks to handle. */
export function stripRemovedConditions(rules: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, r] of Object.entries(rules)) {
    const conds = r && typeof r === "object" ? (r as { conditions?: unknown }).conditions : undefined;
    out[key] = Array.isArray(conds) ? { ...(r as object), conditions: conds.filter(isKnownField) } : r;
  }
  return out;
}

/** Is this saved condition on a field that still exists? (Non-object entries are kept for the loader.) */
function isKnownField(c: unknown): boolean {
  if (!c || typeof c !== "object") return true;
  const field = (c as { field?: unknown }).field;
  return typeof field === "string" && Object.prototype.hasOwnProperty.call(FIELDS, field);
}

/** A fresh default condition for a field (first preset, sensible operator). */
export function defaultCondition(field: ConditionField): Condition {
  if (field === "every") return { field, op: "eq", value: String(EVERY_DEFAULT) };
  const ops = operatorsFor(field);
  return { field, op: ops[0]!.key, value: FIELDS[field].values[0]!.key };
}

// ---- conflict detection ----------------------------------------------------
// Stops players from setting CONTRADICTORY conditions. With the AND combiner, two
// conditions on the same field that can never both be true are a conflict (e.g.
// "wind ≥ Gale AND wind ≤ Calm", or "weather is Clear AND weather is not Clear",
// or two different "weather is X" equals). With OR they can't conflict (either may
// satisfy), so we only flag AND rules. Returns the conflicting condition indices.

/** Does the live value `ord`/`key` satisfy condition (field-local check)? */
function satisfies(field: ConditionField, op: Operator, valueKey: string, candKey: string, candOrd: number): boolean {
  if (op === "eq") return candKey === valueKey;
  if (op === "ne") return candKey !== valueKey;
  const target = ordinalOf(field, valueKey);
  switch (op) {
    case "ge": return candOrd >= target;
    case "le": return candOrd <= target;
    case "gt": return candOrd > target;
    case "lt": return candOrd < target;
    default: return true;
  }
}

/**
 * Find conflicting condition indices in a rule. Only AND rules can conflict. For
 * each field used, we check whether ANY of that field's preset values can satisfy
 * ALL of the rule's conditions on that field — if none can, those conditions
 * conflict (the AND is unsatisfiable). Returns the set of conflicting indices.
 */
export function conflictingIndices(rule: Rule): Set<number> {
  const bad = new Set<number>();
  if (rule.combiner !== "and") return bad;
  // group condition indices by field
  const byField = new Map<ConditionField, number[]>();
  rule.conditions.forEach((c, i) => {
    const arr = byField.get(c.field) ?? [];
    arr.push(i); byField.set(c.field, arr);
  });
  for (const [field, idxs] of byField) {
    if (idxs.length < 2) continue;                 // a single condition can't self-conflict
    const presets = FIELDS[field].values;
    // can some preset value satisfy every condition on this field at once?
    const satisfiable = presets.some((p) =>
      idxs.every((i) => {
        const c = rule.conditions[i]!;
        return satisfies(field, c.op, c.value, p.key, p.ordinal);
      }),
    );
    if (!satisfiable) idxs.forEach((i) => bad.add(i));
  }
  return bad;
}

/** True if the rule has no conflicting conditions. */
export function ruleIsValid(rule: Rule): boolean {
  return conflictingIndices(rule).size === 0;
}
